import { Avatar, Box, Card, CardActionArea, Typography } from '@shared/ui';
import Icon from '@shared/ui/components/Icon';
import type { FC } from 'react';

export interface MemberCardProps {
  name: string;
  email: string;
  companyName?: string;
  avatarUrl?: string;
  firstName?: string;
  lastName?: string;
  sessionCount: number;
  onClick: () => void;
}

const MemberCard: FC<MemberCardProps> = ({
  name,
  email,
  companyName,
  avatarUrl,
  firstName,
  lastName,
  sessionCount,
  onClick,
}) => {
  // Personal-email members get a one-person organization named after their email.
  const showCompany =
    !!companyName && companyName.trim().toLowerCase() !== email.trim().toLowerCase();
  const subtitle = showCompany ? `${companyName}  •  ${email}` : email;
  const avatarFirstName = firstName || name.split(' ')[0] || '';
  const avatarLastName = lastName || name.split(' ').slice(1).join(' ') || '';

  return (
    <Card
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: '12px',
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
        border: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ px: 2.5, py: '10px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Avatar
              src={avatarUrl}
              firstName={avatarFirstName}
              lastName={avatarLastName}
              size="m"
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontFamily: 'Basis Grotesque',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '20px',
                  letterSpacing: '-0.25px',
                  color: 'cement.500',
                }}
              >
                {name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Basis Grotesque',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: 'cement.400',
                }}
              >
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              border: '1px solid',
              borderColor: 'cement.400',
              borderRadius: '40px',
              px: 1,
              py: '5px',
              minWidth: '140px',
              justifyContent: 'flex-start',
            }}
          >
            <Icon
              sx={{
                fontSize: '18px',
                color: 'cement.400',
                flexShrink: 0,
              }}
            >
              article
            </Icon>
            <Typography
              sx={{
                fontFamily: 'Basis Grotesque',
                fontWeight: 500,
                fontSize: '12px',
                lineHeight: '16px',
                color: 'cement.400',
                whiteSpace: 'nowrap',
              }}
            >
              {sessionCount} Session{sessionCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};

export default MemberCard;
